import Check from '@mui/icons-material/Check';
import * as profileStyles from './ProfileFlowStep.css';
import { Gap } from '@design-system/components/grid/grid';
import { ConfirmationCodeInput } from '@design-system/components/input/confirmationCodeInput';
import {
  PhoneCountryOption,
  PhoneNumberInput,
} from '@design-system/components/input/phoneNumberInput';
import { Typography } from '@design-system/components/typography/typography';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { CONFIRMATION_CODE_LENGTH } from '../../view-model/register.view-model';
import {
  authVmCopies,
  type RegisterProfileStep,
  type RegisterValues,
  type RegisterStepFormState,
  type RegisterStepSharedProps,
} from '../../view-model/register.types';
import {
  normalizeRegistrationInstagram,
  normalizeRegistrationTiktok,
  normalizeRegistrationYoutube,
} from '../../model/registration.model';
import { AuthLayout } from '../components/AuthLayout';
import * as styles from '../register.view.css';
import {
  StepButton,
  StepForm,
  StepInput,
  StepTitle,
  toSignupFormId,
  toStepSubmitState,
  toSignupTestId,
} from './StepFormPrimitives';
import { CodeResendHelp } from './CodeResendHelp';

const CONFIRMATION_CODE_INPUT_ARIA_LABEL = 'Confirmation code';
const CODE_KEYWORD_REGEX = /code/i;

type ProfileFlowStepProps = {
  step: RegisterProfileStep;
  values: RegisterValues;
  formState: RegisterStepFormState<RegisterProfileStep>;
  options: Array<PhoneCountryOption>;
  testimonialsContent: ReactNode;
  footerProgress: ReactNode;
} & RegisterStepSharedProps;

const toDisplayName = (firstName: string): string => {
  const normalized = firstName.trim();
  return normalized || 'creator';
};

const getTitle = (step: RegisterProfileStep, firstName: string): ReactNode => {
  switch (step) {
    case 'name':
      return 'Hi, nice to meet you 👋';
    case 'instagram':
      return (
        <>
          {`Connect your socials, ${toDisplayName(firstName)}`}
          <span aria-hidden> 🤳</span>
        </>
      );
    case 'phone':
      return "What's your phone number?";
    case 'code':
      return 'Enter verification code';
  }
};

const getSubtitle = (step: RegisterProfileStep): string | null => {
  switch (step) {
    case 'name':
      return null;
    case 'instagram':
      return "We'll use these to build your Playbook profile and personalize your onboarding.";
    case 'phone':
      return 'Enter your phone number to receive news & updates about the platform.';
    case 'code':
      return null;
  }
};

const isFilled = (value: string): boolean => value.trim().length > 0;

const PresenceCheck = ({ visible }: { visible: boolean }) => {
  if (!visible) {
    return null;
  }

  return (
    <span
      data-testid="social-input-check"
      aria-hidden
      className={profileStyles.checkBadge}
    >
      <Check style={{ fontSize: 14, color: '#FFFFFF' }} />
    </span>
  );
};

const toDigitsOnly = (value: string): string => value.replace(/\D/g, '');

const toCodeRegex = (): RegExp => {
  return new RegExp(`\\b\\d{${CONFIRMATION_CODE_LENGTH}}\\b`);
};

const toCodeFromCodeKeywordContext = (value: string): string | null => {
  const lines = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const codeRegex = toCodeRegex();

  for (const [lineIndex, line] of lines.entries()) {
    if (!CODE_KEYWORD_REGEX.test(line)) {
      continue;
    }

    const sameLineMatch = line.match(codeRegex);
    if (sameLineMatch) {
      return sameLineMatch[0];
    }

    const nearbyChunk = lines.slice(lineIndex + 1, lineIndex + 9).join('\n');
    const nearbyContiguousMatch = nearbyChunk.match(codeRegex);
    if (nearbyContiguousMatch) {
      return nearbyContiguousMatch[0];
    }

    const nearbyDigits = toDigitsOnly(nearbyChunk);
    if (nearbyDigits.length >= CONFIRMATION_CODE_LENGTH) {
      return nearbyDigits.slice(0, CONFIRMATION_CODE_LENGTH);
    }
  }

  return null;
};

const toSanitizedConfirmationCode = (value: string): string => {
  const fromCodeContext = toCodeFromCodeKeywordContext(value);
  if (fromCodeContext) {
    return fromCodeContext;
  }

  const codeRegex = toCodeRegex();
  const standaloneMatch = value.match(codeRegex);
  if (standaloneMatch) {
    return standaloneMatch[0];
  }

  return toDigitsOnly(value).slice(0, CONFIRMATION_CODE_LENGTH);
};

const shouldAutoFocusCodeInput = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  if (typeof window.matchMedia !== 'function') {
    return true;
  }

  return !window.matchMedia('(pointer: coarse)').matches;
};

const isEditableEventTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    target.isContentEditable
  );
};

export const ProfileFlowStep = (p: ProfileFlowStepProps) => {
  const isCodeStep = p.step === 'code';
  const handleRestartFlow = () => p.dispatch('restartFlow');
  const subtitle = getSubtitle(p.step);
  const submitState = toStepSubmitState(p.formState);
  const setValue = <TField extends keyof RegisterValues>(
    field: TField,
    value: RegisterValues[TField],
  ): void => {
    p.dispatch('setField', {
      step: p.step,
      field,
      value,
    });
  };
  const setCodeValue = (value: RegisterValues['code']): void => {
    p.dispatch('setField', {
      step: 'code',
      field: 'code',
      value,
    });
  };
  const codeInputContainerRef = useRef<HTMLDivElement | null>(null);
  const selectedCountry =
    p.options.find(
      (option) => option.dialCode === p.values.phoneCountryDialCode,
    ) ?? p.options[0];

  const focusConfirmationCodeInput = useCallback((): void => {
    const codeInput =
      codeInputContainerRef.current?.querySelector<HTMLInputElement>(
        `input[aria-label="${CONFIRMATION_CODE_INPUT_ARIA_LABEL}"]`,
      );
    if (!codeInput || codeInput.disabled) {
      return;
    }

    codeInput.focus();
    const cursorPosition = codeInput.value.length;
    codeInput.setSelectionRange(cursorPosition, cursorPosition);
  }, []);

  useEffect(() => {
    if (!isCodeStep || !shouldAutoFocusCodeInput()) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      focusConfirmationCodeInput();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [focusConfirmationCodeInput, isCodeStep]);

  useEffect(() => {
    if (!isCodeStep) {
      return;
    }

    const handleWindowPaste = (event: ClipboardEvent): void => {
      if (event.defaultPrevented) {
        return;
      }

      const pastedText = event.clipboardData?.getData('text') ?? '';
      const sanitizedCode = toSanitizedConfirmationCode(pastedText);
      if (sanitizedCode.length === 0) {
        return;
      }

      event.preventDefault();
      focusConfirmationCodeInput();
      setCodeValue(sanitizedCode);
    };

    window.addEventListener('paste', handleWindowPaste);
    return () => {
      window.removeEventListener('paste', handleWindowPaste);
    };
  }, [focusConfirmationCodeInput, isCodeStep, p.dispatch]);

  useEffect(() => {
    if (!isCodeStep) {
      return;
    }
    const currentCode = p.values.code;

    const handleWindowKeyDown = (event: KeyboardEvent): void => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }

      const activeElement = document.activeElement;
      const codeInput =
        codeInputContainerRef.current?.querySelector<HTMLInputElement>(
          `input[aria-label="${CONFIRMATION_CODE_INPUT_ARIA_LABEL}"]`,
        );
      const isCodeInputFocused = activeElement === codeInput;
      if (isCodeInputFocused) {
        return;
      }

      if (isEditableEventTarget(event.target)) {
        return;
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        focusConfirmationCodeInput();
        setCodeValue(currentCode.slice(0, -1));
        return;
      }

      if (!/^\d$/.test(event.key)) {
        return;
      }

      event.preventDefault();
      focusConfirmationCodeInput();
      setCodeValue(
        `${currentCode}${event.key}`.slice(0, CONFIRMATION_CODE_LENGTH),
      );
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown);
    };
  }, [focusConfirmationCodeInput, isCodeStep, p.dispatch, p.values.code]);

  const isSocialsStep = p.step === 'instagram';
  const titleGap = isSocialsStep ? 'm' : '3xl';
  const subtitleGap = isSocialsStep ? 'l' : '3xl';
  const formGap = isSocialsStep ? 'l' : '3xl';

  return (
    <AuthLayout
      preset={isCodeStep ? 'register-two-column' : 'register-single-column'}
      rightColumn={isCodeStep ? p.testimonialsContent : undefined}
      onLogoClick={handleRestartFlow}
      bodyKey={`register-step-${p.step}`}
      boxClassName={isSocialsStep ? profileStyles.socialsBox : undefined}
      bodyClassName={isSocialsStep ? profileStyles.socialsBody : undefined}
      headerClassName={isSocialsStep ? profileStyles.socialsHeader : undefined}
      content={
        <div className={styles.codeStepStack}>
          <StepTitle>{getTitle(p.step, p.values.firstName)}</StepTitle>
          <Gap height={titleGap} />
          {isCodeStep ? (
            <>
              <Typography
                variant="body"
                size="large"
                color="N-80"
                className={styles.codeSubtitleText}
              >
                We&apos;ve sent a code to your email:
                <br />
                <strong>{p.values.email}</strong>
              </Typography>
              <Gap height="3xl" />
            </>
          ) : null}
          {subtitle ? (
            <>
              <Typography
                variant="body"
                size="large"
                color="N-80"
                className={styles.subtitleText}
              >
                {subtitle}
              </Typography>
              <Gap height={subtitleGap} />
            </>
          ) : null}
          <StepForm
            id={toSignupFormId(p.step)}
            step={p.step}
            onSubmit={() => {
              if (isCodeStep) {
                p.dispatch('submitCode');
                return;
              }

              p.dispatch('continue');
            }}
          >
            {p.step === 'name' ? (
              <div className={styles.nameInputsStack}>
                <StepInput
                  variant="text"
                  autoFocus={p.shouldAutoFocusInput}
                  label="First Name"
                  value={p.values.firstName}
                  testId={toSignupTestId('name', 'first-name', 'input')}
                  onValueChange={(value) => setValue('firstName', value)}
                  disabled={submitState.type === 'submitting'}
                />
                <StepInput
                  variant="text"
                  label="Last Name"
                  value={p.values.lastName}
                  testId={toSignupTestId('name', 'last-name', 'input')}
                  onValueChange={(value) => setValue('lastName', value)}
                  disabled={submitState.type === 'submitting'}
                />
              </div>
            ) : null}

            {p.step === 'instagram' ? (
              <div
                role="group"
                aria-label="Social profiles"
                className={styles.nameInputsStack}
              >
                <StepInput
                  variant="social"
                  platform="instagram"
                  label="Instagram"
                  ariaLabel="Instagram handle"
                  value={p.values.instagram}
                  testId={toSignupTestId('instagram', 'instagram', 'input')}
                  onValueChange={(value) =>
                    setValue('instagram', normalizeRegistrationInstagram(value))
                  }
                  autoFocus={p.shouldAutoFocusInput}
                  disabled={submitState.type === 'submitting'}
                  endCta={
                    <PresenceCheck visible={isFilled(p.values.instagram)} />
                  }
                />
                <StepInput
                  variant="social"
                  platform="youtube"
                  label="YouTube"
                  ariaLabel="YouTube handle"
                  value={p.values.youtube}
                  testId={toSignupTestId('instagram', 'youtube', 'input')}
                  onValueChange={(value) =>
                    setValue('youtube', normalizeRegistrationYoutube(value))
                  }
                  disabled={submitState.type === 'submitting'}
                  endCta={
                    <PresenceCheck visible={isFilled(p.values.youtube)} />
                  }
                />
                <StepInput
                  variant="social"
                  platform="tiktok"
                  label="Tik Tok"
                  ariaLabel="TikTok handle"
                  value={p.values.tiktok}
                  testId={toSignupTestId('instagram', 'tiktok', 'input')}
                  onValueChange={(value) =>
                    setValue('tiktok', normalizeRegistrationTiktok(value))
                  }
                  disabled={submitState.type === 'submitting'}
                  endCta={<PresenceCheck visible={isFilled(p.values.tiktok)} />}
                />
              </div>
            ) : null}

            {p.step === 'phone' ? (
              <PhoneNumberInput
                options={p.options}
                selectedDialCode={selectedCountry.dialCode}
                phoneNumber={p.values.phoneNumber}
                autoFocus={p.shouldAutoFocusInput}
                testId={toSignupTestId('phone', 'phone', 'input')}
                onDialCodeChange={(value) =>
                  setValue('phoneCountryDialCode', value)
                }
                onPhoneNumberChange={(value) => setValue('phoneNumber', value)}
                disabled={submitState.type === 'submitting'}
                inputAriaLabel="Phone number"
              />
            ) : null}

            {p.step === 'code' ? (
              <div ref={codeInputContainerRef}>
                <ConfirmationCodeInput
                  autoFocus={p.shouldAutoFocusInput}
                  aria-label={CONFIRMATION_CODE_INPUT_ARIA_LABEL}
                  length={CONFIRMATION_CODE_LENGTH}
                  value={p.values.code}
                  testId={toSignupTestId('code', 'code', 'input')}
                  onChange={(value) => setCodeValue(value)}
                  onPaste={(event) => {
                    const pastedText = event.clipboardData.getData('text');
                    const sanitizedCode =
                      toSanitizedConfirmationCode(pastedText);
                    if (sanitizedCode.length === 0) {
                      return;
                    }

                    event.preventDefault();
                    setCodeValue(sanitizedCode);
                  }}
                  error={
                    submitState.type === 'error'
                      ? submitState.message
                      : undefined
                  }
                  hideError
                  disabled={submitState.type === 'submitting'}
                />
              </div>
            ) : null}

            {submitState.type === 'error' ? (
              <Typography
                role="alert"
                variant="body"
                size="small"
                className={
                  p.step === 'code' ? styles.codeInputError : styles.errorText
                }
              >
                {submitState.message}
              </Typography>
            ) : null}

            <Gap height={formGap} />
            {isCodeStep ? (
              <CodeResendHelp
                disabled={submitState.type === 'submitting'}
                onResendCode={() => p.dispatch('resendCode')}
              />
            ) : (
              <StepButton
                testId={toSignupTestId(p.step, 'continue', 'button')}
                loading={submitState.type === 'submitting'}
                disabled={!p.canContinue || submitState.type === 'submitting'}
              >
                {authVmCopies.common.continue}
              </StepButton>
            )}
          </StepForm>
        </div>
      }
      footer={
        isCodeStep ? (
          <div className={styles.codeFooterSpacer} aria-hidden />
        ) : (
          p.footerProgress
        )
      }
    />
  );
};
